import { useState } from 'react';
import Input from '../common/Input';
import Button from '../common/Button';

const textareaClass = `
  w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-900 bg-white
  placeholder:text-slate-400 shadow-inner-sm
  hover:border-slate-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-500/20 focus:outline-none
  transition-all duration-150 resize-none
`;

const datetimeClass = `
  w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-900 bg-white
  hover:border-slate-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-500/20 focus:outline-none
  transition-all duration-150 shadow-inner-sm
`;

const FieldLabel = ({ children, optional }) => (
  <label className="block text-sm font-medium text-slate-700 mb-1.5">
    {children}
    {optional && <span className="text-slate-400 text-xs font-normal ml-1">(optional)</span>}
  </label>
);

const generateCode = () => Math.random().toString(36).substring(2, 8).toUpperCase();

const ExamForm = ({ form, setForm, onSubmit, loading, submitLabel = 'Save Exam' }) => {
  const [showCode, setShowCode] = useState(false);
  return (
  <form onSubmit={onSubmit} className="space-y-5">
    <Input
      label="Exam Title"
      value={form.title}
      onChange={(e) => setForm({ ...form, title: e.target.value })}
      placeholder="e.g. JavaScript Fundamentals Assessment"
      required
    />

    <div>
      <FieldLabel optional>Description</FieldLabel>
      <textarea
        rows={3}
        className={textareaClass}
        value={form.description}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
        placeholder="Brief overview of what this exam covers…"
      />
    </div>

    <div className="grid grid-cols-2 gap-4">
      <Input
        label="Duration (minutes)"
        type="number"
        min="1"
        max="480"
        value={form.duration_minutes}
        onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}
        required
      />
      <Input
        label="Pass Marks"
        type="number"
        min="0"
        value={form.pass_marks}
        onChange={(e) => setForm({ ...form, pass_marks: e.target.value })}
      />
    </div>

    <div className="grid grid-cols-2 gap-4">
      <Input
        label="Max Tab Switches"
        type="number"
        min="1"
        max="20"
        value={form.max_tab_switches}
        onChange={(e) => setForm({ ...form, max_tab_switches: e.target.value })}
        hint="Auto-submit after this many tab switches"
      />
      <div className="flex items-center gap-3 mt-6">
        <div
          onClick={() => setForm({ ...form, allow_result_view: !form.allow_result_view })}
          className={`relative w-10 h-5.5 h-6 rounded-full cursor-pointer transition-colors duration-200 ${
            form.allow_result_view ? 'bg-primary-600' : 'bg-slate-300'
          }`}
        >
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200 ${
            form.allow_result_view ? 'translate-x-4' : ''
          }`} />
          <input
            id="allow_result"
            type="checkbox"
            checked={form.allow_result_view}
            onChange={(e) => setForm({ ...form, allow_result_view: e.target.checked })}
            className="sr-only"
          />
        </div>
        <label htmlFor="allow_result" className="text-sm text-slate-700 cursor-pointer select-none">
          Candidates can view result
        </label>
      </div>
      <div className="flex items-center gap-3">
        <div
          onClick={() => setForm({ ...form, shuffle_questions: !form.shuffle_questions })}
          className={`relative w-10 h-6 rounded-full cursor-pointer transition-colors duration-200 ${
            form.shuffle_questions ? 'bg-primary-600' : 'bg-slate-300'
          }`}
        >
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200 ${
            form.shuffle_questions ? 'translate-x-4' : ''
          }`} />
          <input
            id="shuffle_questions"
            type="checkbox"
            checked={form.shuffle_questions || false}
            onChange={(e) => setForm({ ...form, shuffle_questions: e.target.checked })}
            className="sr-only"
          />
        </div>
        <label htmlFor="shuffle_questions" className="text-sm text-slate-700 cursor-pointer select-none">
          Shuffle question order per candidate
        </label>
      </div>
    </div>

    <div>
      <FieldLabel optional>Exam Instructions</FieldLabel>
      <textarea
        rows={4}
        className={textareaClass}
        value={form.instructions}
        onChange={(e) => setForm({ ...form, instructions: e.target.value })}
        placeholder="List exam rules and instructions here…"
      />
    </div>

    <div className="grid grid-cols-2 gap-4">
      <div>
        <FieldLabel optional>Scheduled Start</FieldLabel>
        <input
          type="datetime-local"
          className={datetimeClass}
          value={form.scheduled_start}
          onChange={(e) => setForm({ ...form, scheduled_start: e.target.value })}
        />
      </div>
      <div>
        <FieldLabel optional>Scheduled End</FieldLabel>
        <input
          type="datetime-local"
          className={datetimeClass}
          value={form.scheduled_end}
          onChange={(e) => setForm({ ...form, scheduled_end: e.target.value })}
        />
      </div>
    </div>

    {/* Negative marking */}
    <div className="grid grid-cols-2 gap-4">
      <div>
        <FieldLabel optional>Negative Marks per Wrong Answer</FieldLabel>
        <input
          type="number"
          min="0"
          step="0.25"
          className={datetimeClass}
          value={form.negative_marks_per_wrong ?? 0}
          onChange={(e) => setForm({ ...form, negative_marks_per_wrong: parseFloat(e.target.value) || 0 })}
          placeholder="0"
        />
        <p className="text-xs text-slate-400 mt-1">0 = no penalty for wrong answers.</p>
      </div>
    </div>

    {/* Access code — mandatory */}
    <div>
      <FieldLabel>Access Code <span className="text-red-500 text-xs font-normal ml-1">Required</span></FieldLabel>
      <div className="flex gap-2">
        <input
          type={showCode ? 'text' : 'password'}
          className={datetimeClass}
          value={form.access_code || ''}
          onChange={(e) => setForm({ ...form, access_code: e.target.value })}
          placeholder="Access code"
          maxLength={50}
          required
        />
        <button
          type="button"
          onClick={() => setShowCode((s) => !s)}
          className="px-3 py-2 rounded-xl border border-slate-200 text-xs text-slate-500 hover:border-slate-300 transition-colors"
        >
          {showCode ? 'Hide' : 'Show'}
        </button>
        <button
          type="button"
          onClick={() => setForm({ ...form, access_code: generateCode() })}
          className="px-3 py-2 rounded-xl border border-slate-200 text-xs text-slate-500 hover:border-slate-300 transition-colors whitespace-nowrap"
        >
          Regenerate
        </button>
      </div>
      <p className="text-xs text-slate-400 mt-1">All candidates must enter this code to access the exam. It is emailed automatically when you assign candidates.</p>
    </div>

    <Button type="submit" fullWidth size="lg" loading={loading}>
      {submitLabel}
    </Button>
  </form>
  );
};

export default ExamForm;
